import { z } from 'zod';

export type IntegrationProviderKind = 'notification' | 'webhook' | 'sync';
export type IntegrationProviderAuthStrategy = 'none' | 'token' | 'oauth2';

export interface IntegrationProviderDef {
	label: string;
	description: string;
	kind: IntegrationProviderKind;
	authStrategy: IntegrationProviderAuthStrategy;
	requiresConnection: boolean;
	connectionConfigSchema: z.ZodType;
	subscriptionTargetSchema: z.ZodType;
	// How many records one delivery may carry. Zapier treats an array payload as
	// one Zap run per element, which turns a 5,000-record backfill into ~50
	// requests against their 30/sec per-URL cap. A plain customer webhook is 1.
	maxBatchSize?: number;
	// What the connect form asks for when authStrategy is 'token'. It lives here
	// so the admin connect dialog stays provider-agnostic: a new token provider
	// declares its own wording rather than adding a branch to the component.
	credential?: {
		label: string;
		placeholder: string;
		help: string;
	};
	// The connect page's walkthrough for a key the customer creates in the other
	// app. Code owns it because every step follows from how the adapter signs in.
	setup?: {
		steps: string[];
		note?: string;
	};
}

const webhookTarget = z.object({
	url: z.string().url()
});

const zapierTarget = z.object({
	url: z.string().url()
});

const slackTarget = z.object({
	channelId: z.string().min(1),
	channelName: z.string().min(1)
});

const slackConnectionConfig = z.object({
	teamName: z.string().nullable().optional()
});

export const MAX_RULE_EMAILS = 10;

export const emailTargetSchema = z
	.object({
		memberUserIds: z.array(z.string().min(1)).default([]),
		emails: z.array(z.string().trim().toLowerCase().email()).max(MAX_RULE_EMAILS).default([]),
		// System scope only; the subscription service refuses it anywhere else.
		eventOwner: z.literal(true).optional()
	})
	.refine((t) => t.memberUserIds.length + t.emails.length > 0 || t.eventOwner === true, {
		message: 'Add at least one recipient'
	});
export type EmailTarget = z.infer<typeof emailTargetSchema>;

// The API key is the whole credential and the account id arrives on the token
// response, so a Wild Apricot connection has nothing left to configure.
const wildApricotConnectionConfig = z.strictObject({});

// A sync source is read from on a schedule, never delivered to, so it can
// have no subscription: a target schema that accepts nothing refuses one at
// both subscription services rather than storing a row no adapter can send.
const noSubscriptionTarget = z.never();

export const INTEGRATION_PROVIDERS = {
	webhook: {
		label: 'Webhook',
		description: 'Post record events to an HTTPS endpoint you control',
		kind: 'webhook',
		authStrategy: 'none',
		requiresConnection: false,
		connectionConfigSchema: z.object({}),
		subscriptionTargetSchema: webhookTarget,
		maxBatchSize: 1
	},
	zapier: {
		label: 'Zapier',
		description: 'Trigger a Zap when a record event happens',
		kind: 'webhook',
		authStrategy: 'none',
		requiresConnection: false,
		connectionConfigSchema: z.object({}),
		subscriptionTargetSchema: zapierTarget,
		maxBatchSize: 100
	},
	slack: {
		label: 'Slack',
		description: 'Post event notifications into a Slack channel',
		kind: 'notification',
		authStrategy: 'token',
		requiresConnection: true,
		connectionConfigSchema: slackConnectionConfig,
		subscriptionTargetSchema: slackTarget,
		credential: {
			label: 'Bot token',
			placeholder: 'xoxb-...',
			help: 'Found under OAuth & Permissions once the app is installed.'
		}
	},
	email: {
		label: 'Email',
		description: 'Email event activity and digests to your team',
		kind: 'notification',
		authStrategy: 'none',
		requiresConnection: false,
		connectionConfigSchema: z.object({}),
		subscriptionTargetSchema: emailTargetSchema
	},
	wild_apricot: {
		label: 'Wild Apricot',
		description: 'Keep an invite list in step with your Wild Apricot membership',
		kind: 'sync',
		authStrategy: 'token',
		requiresConnection: true,
		connectionConfigSchema: wildApricotConnectionConfig,
		subscriptionTargetSchema: noSubscriptionTarget,
		credential: {
			label: 'API key',
			placeholder: 'Paste the API key from Wild Apricot',
			help: 'In Wild Apricot, open Apps, then Authorized applications, then Authorize application, and choose Server application.'
		},
		setup: {
			steps: [
				'In Wild Apricot, open Apps, then Authorized applications.',
				'Choose Authorize application, then Server application, and continue.',
				'Name it Sunfish and choose Read only. Sunfish never changes anything in Wild Apricot.',
				'Leave the client ID blank, don’t generate a client secret, and leave single sign-on unchecked. Save.',
				'Copy the API key shown at the top of that page and paste it into Sunfish.'
			],
			note: 'Anyone holding the key can read your Wild Apricot account. Paste it here and nowhere else, and delete it in Wild Apricot if you stop using Sunfish.'
		}
	}
} as const satisfies Record<string, IntegrationProviderDef>;

export type IntegrationProviderKey = keyof typeof INTEGRATION_PROVIDERS;

// Only providers that actually hold credentials can be "connected". A
// connectionless provider (a plain customer webhook) has nothing to connect, so
// surfaces like the connect dialog and its setup copy are keyed by this rather
// than by every provider.
export type ConnectableProviderKey = {
	[K in IntegrationProviderKey]: (typeof INTEGRATION_PROVIDERS)[K]['requiresConnection'] extends true
		? K
		: never;
}[IntegrationProviderKey];

// The connect dialog on the notifications surface can only save a notification
// credential; a sync source connects from the invite list it fills.
export type NotificationConnectableProviderKey = {
	[K in ConnectableProviderKey]: (typeof INTEGRATION_PROVIDERS)[K]['kind'] extends 'notification'
		? K
		: never;
}[ConnectableProviderKey];

export type SyncProviderKey = {
	[K in IntegrationProviderKey]: (typeof INTEGRATION_PROVIDERS)[K]['kind'] extends 'sync' ? K : never;
}[IntegrationProviderKey];

export function isSyncProviderKey(value: string): value is SyncProviderKey {
	return isIntegrationProviderKey(value) && INTEGRATION_PROVIDERS[value].kind === 'sync';
}

export function isIntegrationProviderKey(value: string): value is IntegrationProviderKey {
	return Object.prototype.hasOwnProperty.call(INTEGRATION_PROVIDERS, value);
}

// A partner app registered over OAuth carries its own client key as the
// provider, so the provider space is open-ended and cannot be a closed union.
// The shape is still checked: an unbounded string would surface as a failed
// insert rather than as a refusal at the boundary that accepted it. 64 is both
// oauth_clients.client_key's width and every provider column's, so any
// registerable client key is storable as a provider.
const PROVIDER_KEY_SHAPE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function isProviderKeyShape(value: string): boolean {
	return PROVIDER_KEY_SHAPE.test(value);
}

/**
 * The definition a provider behaves by.
 *
 * An unregistered provider is a partner app's client key. "An HTTPS endpoint we
 * post signed JSON to" is exactly what the webhook provider is, so that is what
 * such a partner inherits - target shape, batch size and, through the record
 * plane's adapter lookup, Standard Webhooks signing and ordinary retries. A
 * registered provider always keeps its own definition, so Zapier stays Zapier.
 */
export function integrationProviderDefinition(provider: string): IntegrationProviderDef {
	return isIntegrationProviderKey(provider)
		? INTEGRATION_PROVIDERS[provider]
		: INTEGRATION_PROVIDERS.webhook;
}
